import React from 'react';
import { OutgoingTransactionsRowProps } from './types';
import { composeClassName } from '@/utils/util_helpers';
import { Table } from '@/components/design-system/molecules/table';
import { useTranslation } from 'react-i18next';

const OutgoingTransactionTableHeader: React.FC<{ children: string }> = ({ children }) => {
  return <th className="text-start text-footnote1 text-text-tertiary">{children}</th>;
};

type LinkConfig = {
  href: string;
  label: string;
};

const buildContractLink = (
  outgoingTransaction: OutgoingTransactionsRowProps['outgoingTransaction'],
  label: string
): LinkConfig | null => {
  if (!outgoingTransaction.contract_id) {
    return null;
  }

  return {
    href: `/admin/contracts/${outgoingTransaction.contract_id}`,
    label,
  };
};

const buildSecurityDepositLink = (
  outgoingTransaction: OutgoingTransactionsRowProps['outgoingTransaction'],
  labels: {
    securityDeposit: string;
  }
): LinkConfig | null => {
  if (outgoingTransaction.linked_security_deposit?.security_deposit_id) {
    return {
      href: `/admin/security_deposits/${outgoingTransaction.linked_security_deposit.security_deposit_id}`,
      label: labels.securityDeposit,
    };
  }

  return null;
};

//todo: check to add payout link
const buildPayoutLink = (
  outgoingTransaction: OutgoingTransactionsRowProps['outgoingTransaction'],
  label: string
): LinkConfig | null => {
  if (outgoingTransaction.household) {
    return null;
  }

  return {
    href: '/admin/transactions#payouts',
    label,
  };
};

const getLinkForCategory = (
  outgoingTransaction: OutgoingTransactionsRowProps['outgoingTransaction'],
  t: ReturnType<typeof useTranslation>['t']
) => {
  // Check if there's a linked security deposit transaction first (for both compensation and deposit_refund)
  const securityDepositLink = buildSecurityDepositLink(outgoingTransaction, {
    securityDeposit:
      t('js.features.admin.transactions.outgoing_transactions_list.links.security_deposit') ?? '',
  });

  switch (outgoingTransaction.category) {
    case 'rent_refund':
    case 'cleaning_refund':
      return {
        hidden: false,
        link: buildContractLink(
          outgoingTransaction,
          t('js.features.admin.transactions.outgoing_transactions_list.links.contract') ?? ''
        ),
      };
    case 'deposit_refund':
      // For deposit_refund, prefer security deposit link if available, otherwise contract link
      return {
        hidden: false,
        link: securityDepositLink ?? buildContractLink(
          outgoingTransaction,
          t('js.features.admin.transactions.outgoing_transactions_list.links.contract') ?? ''
        ),
      };
    case 'compensation':
      return {
        hidden: false,
        link: securityDepositLink,
      };
    case 'payout':
      return {
        hidden: false,
        link: buildPayoutLink(
          outgoingTransaction,
          t('js.features.admin.transactions.outgoing_transactions_list.links.payouts') ?? ''
        ),
      };
    case 'utility':
    case 'repair':
      return {
        hidden: true,
      };
    default:
      return {
        hidden: false,
        link: null,
      };
  }
};

const OutgoingTransactionsTable: React.FC<OutgoingTransactionsRowProps> = ({
  outgoingTransaction,
}) => {
  const { t } = useTranslation();
  const { hidden, link } = getLinkForCategory(outgoingTransaction, t);

  const ibanValue =
    outgoingTransaction.recipient_iban ??
    t('js.features.admin.transactions.outgoing_transactions_list.links.not_available');
  const externalPaymentId =
    outgoingTransaction.payment?.external_payment_id ??
    t('js.features.admin.transactions.outgoing_transactions_list.links.not_available');

  const renderLinkContent = () => {
    if (hidden) {
      return t('js.features.admin.transactions.outgoing_transactions_list.links.hidden');
    }

    if (!link) {
      return t('js.features.admin.transactions.outgoing_transactions_list.links.not_available');
    }

    return (
      <a
        className="text-text-yellow hover:underline"
        href={link.href}
        target="_blank"
        rel="noreferrer"
      >
        {link.label}
      </a>
    );
  };

  return (
    <table className="w-full">
      <thead className="pb-3">
        <tr>
          <OutgoingTransactionTableHeader>
            {t('js.features.admin.transactions.outgoing_transactions_list.sub_table_columns.iban')}
          </OutgoingTransactionTableHeader>
          <OutgoingTransactionTableHeader>
            {t(
              'js.features.admin.transactions.outgoing_transactions_list.sub_table_columns.external_payment_id'
            )}
          </OutgoingTransactionTableHeader>
          <OutgoingTransactionTableHeader>
            {t(
              'js.features.admin.transactions.outgoing_transactions_list.sub_table_columns.resource'
            )}
          </OutgoingTransactionTableHeader>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Table.TableCell>
            <Table.TableCellDefaultContent>{ibanValue}</Table.TableCellDefaultContent>
          </Table.TableCell>
          <Table.TableCell>
            <Table.TableCellDefaultContent>{externalPaymentId}</Table.TableCellDefaultContent>
          </Table.TableCell>
          <Table.TableCell>
            <Table.TableCellDefaultContent>
              {renderLinkContent() as string}
            </Table.TableCellDefaultContent>
          </Table.TableCell>
        </tr>
      </tbody>
    </table>
  );
};

const OutgoingTransactionsRow: React.FC<OutgoingTransactionsRowProps> = ({
  outgoingTransaction,
  index,
}) => {
  return (
    <div className={composeClassName('w-full', index % 2 === 0 ? 'bg-white' : 'bg-bg-E30')}>
      <div className="flex w-full justify-center">
        <div className="h-[1px] w-full bg-border-E50" />
      </div>
      <div className="mx-5 my-3">
        <OutgoingTransactionsTable outgoingTransaction={outgoingTransaction} index={index} />
      </div>
    </div>
  );
};

export { OutgoingTransactionsRow };
